/**
 * Proposal Packet Store
 * 
 * Manages OST-backed proposal packets for Catchball.
 * Proposal packets are first-class persisted artifacts that include:
 * - Strategic objectives being responded to
 * - OST nodes (opportunities, solutions, experiments)
 * - Readiness snapshots for each item
 * - Comparability state
 * 
 * Per canonical requirements:
 * - Catchball tracks direction, ownership, status, and artifacts (Doc 04)
 * - No invisible work; artifacts must persist and be reviewable (Doc 01)
 * - Proposals allowed pre-Arrange; readiness disclosure required (Doc 03)
 */

import { create } from 'zustand';
import { getSafeSupabaseClient } from '../../lib/supabaseSync';
import { getTypedSupabaseClient } from '../../lib/typedSupabaseClient';
import {
  acceptCatchballProposal,
  draftCatchballProposal,
  rejectCatchballProposal,
  returnCatchballProposal,
  submitCatchballProposal,
  type CatchballDecisionResult,
} from '../../lib/catchball/proposalDecisions';
import type {
  CatchballProposal,
  CatchballThreadEntry,
  CreateProposalPacketParams,
  ExecutiveResponseParams,
  ReadinessSnapshot,
  ItemReadiness,
  StructuredBlockers,
  ProposalSummary,
  ObjectiveWithProposals,
} from '../../types/prioritization/catchballProposal';

// ============================================================================
// Store State
// ============================================================================

interface ProposalPacketState {
  proposals: CatchballProposal[];
  threadEntries: CatchballThreadEntry[];
  isLoading: boolean;
  error: string | null;
  
  // ========================================================================
  // Proposal CRUD
  // ========================================================================
  
  /**
   * Fetch proposals for a cycle.
   */
  fetchProposals: (cycleId: string) => Promise<void>;

  /**
   * Fetch every proposal in a workspace, replacing the list. Reads through each
   * proposal's cycle because `workspace_id` can be empty on older rows.
   */
  fetchProposalsForWorkspace: (workspaceId: string) => Promise<void>;
  
  /**
   * Fetch proposals for a specific loop.
   */
  fetchProposalsForLoop: (loopId: string) => Promise<CatchballProposal[]>;
  
  /**
   * Fetch a single proposal by ID (e.g. for the proposal review screen).
   */
  fetchProposalById: (proposalId: string) => Promise<CatchballProposal | null>;
  
  /**
   * Get proposals by objective ID (for executive inbox).
   */
  getProposalsByObjective: (objectiveId: string) => CatchballProposal[];
  
  /**
   * Get packet proposals only.
   */
  getPacketProposals: (cycleId: string) => CatchballProposal[];
  
  /**
   * Create a proposal packet from OST selection.
   */
  createProposalPacket: (params: CreateProposalPacketParams) => Promise<CatchballProposal | null>;
  
  /**
   * Submit a proposal (transitions loop state).
   */
  submitProposal: (proposalId: string, actorId: string) => Promise<boolean>;
  
  // ========================================================================
  // Executive Responses
  // ========================================================================
  
  /**
   * Accept a proposal provisionally.
   */
  acceptProvisional: (params: ExecutiveResponseParams) => Promise<boolean>;
  
  /**
   * Return a proposal with structured blockers.
   */
  returnWithBlockers: (params: ExecutiveResponseParams) => Promise<boolean>;
  
  /**
   * Reject a proposal.
   */
  rejectProposal: (params: ExecutiveResponseParams) => Promise<boolean>;
  
  // ========================================================================
  // Readiness Computation
  // ========================================================================
  
  /**
   * Compute readiness snapshot for items in a cycle.
   */
  computeReadinessSnapshot: (cycleId: string, itemIds: string[]) => Promise<ReadinessSnapshot>;
  
  /**
   * Get per-item readiness from database.
   */
  getItemReadiness: (cycleId: string, itemId: string) => Promise<ItemReadiness | null>;
  
  // ========================================================================
  // Thread Management
  // ========================================================================
  
  /**
   * Fetch thread entries for a proposal.
   */
  fetchThreadForProposal: (proposalId: string) => Promise<CatchballThreadEntry[]>;
  
  // ========================================================================
  // Selectors
  // ========================================================================
  
  getProposal: (proposalId: string) => CatchballProposal | undefined;
  getProposalSummaries: (cycleId: string) => ProposalSummary[];
  getObjectivesWithProposals: (cycleId: string) => ObjectiveWithProposals[];
  
  // ========================================================================
  // Utilities
  // ========================================================================
  
  clearError: () => void;
}

// ============================================================================
// DB Row Mapping (imported from types but defined inline for bundling)
// ============================================================================

/** The most rows one read returns from the database (the project's default). */
const PROPOSALS_PAGE_SIZE = 1000;

function mapRowToProposal(row: Record<string, unknown>): CatchballProposal {
  return {
    id: row.id as string,
    cycleId: row.cycle_id as string,
    proposalType: (row.proposal_type as CatchballProposal['proposalType']) || 'item',
    loopId: row.loop_id as string | undefined,
    itemId: row.item_id as string | undefined,
    itemTitle: row.item_title as string | undefined,
    ostNodeIds: row.ost_node_ids as string[] | undefined,
    objectiveIds: row.objective_ids as string[] | undefined,
    readinessSnapshot: row.readiness_snapshot as ReadinessSnapshot | undefined,
    comparabilityState: row.comparability_state as CatchballProposal['comparabilityState'],
    parentProposalId: row.parent_proposal_id as string | undefined,
    proposedBy: row.proposed_by as string | undefined,
    proposedByName: row.proposed_by_name as string | undefined,
    proposedByOrgUnit: row.proposed_by_org_unit as string | undefined,
    proposedByOrgUnitName: row.proposed_by_org_unit_name as string | undefined,
    proposedAt: new Date(row.proposed_at as string),
    direction: (row.direction as CatchballProposal['direction']) || 'up',
    targetOrgUnit: row.target_org_unit as string | undefined,
    targetOrgUnitName: row.target_org_unit_name as string | undefined,
    targetRecipients: row.target_recipients as string[] | undefined,
    proposedRank: row.proposed_rank as number | undefined,
    proposedCutLine: row.proposed_cut_line as CatchballProposal['proposedCutLine'],
    proposedAction: row.proposed_action as CatchballProposal['proposedAction'],
    rationale: row.rationale as string,
    supportingEvidence: row.supporting_evidence as string[] | undefined,
    focusListId: row.focus_list_id as string | undefined,
    aggregatedRankList: (row.aggregated_rank_list as string[] | null) ?? undefined,
    sessionId: (row.session_id as string | null) ?? undefined,
    status: (row.status as CatchballProposal['status']) || 'pending',
    resolvedAt: row.resolved_at ? new Date(row.resolved_at as string) : undefined,
    resolvedBy: row.resolved_by as string | undefined,
    resolution: row.resolution as CatchballProposal['resolution'],
    createdAt: new Date(row.created_at as string || row.proposed_at as string),
    updatedAt: new Date(row.updated_at as string || row.proposed_at as string),
    workspaceId: row.workspace_id as string | undefined,
    tenantId: row.tenant_id as string | undefined,
  };
}

function mapRowToThreadEntry(row: Record<string, unknown>): CatchballThreadEntry {
  return {
    id: row.id as string,
    threadId: row.thread_id as string | undefined,
    proposalId: row.proposal_id as string | undefined,
    cycleId: row.cycle_id as string,
    itemId: row.item_id as string | undefined,
    messageType: row.message_type as CatchballThreadEntry['messageType'],
    authorRole: row.author_role as CatchballThreadEntry['authorRole'],
    authorId: row.author_id as string,
    authorName: row.author_name as string | undefined,
    body: row.body as string || row.content as string || '',
    structuredBlockers: row.structured_blockers as StructuredBlockers | undefined,
    createdAt: new Date(row.created_at as string),
    updatedAt: row.updated_at ? new Date(row.updated_at as string) : undefined,
    workspaceId: row.workspace_id as string | undefined,
    tenantId: row.tenant_id as string | undefined,
  };
}

// ============================================================================
// Store Implementation
// ============================================================================

type PacketStoreSet = (
  partial:
    | Partial<ProposalPacketState>
    | ((state: ProposalPacketState) => Partial<ProposalPacketState>),
) => void;

/**
 * Folds a decision outcome into the store: the refusal's own words on
 * failure; on success, the proposal row the RPC returned (DB truth).
 */
function applyDecision(set: PacketStoreSet, proposalId: string, result: CatchballDecisionResult): boolean {
  if (!result.ok) {
    set({ isLoading: false, error: result.error || 'The server refused this action. Nothing was recorded.' });
    return false;
  }
  const row = result.data?.proposal as Record<string, unknown> | undefined;
  if (row) {
    const updated = mapRowToProposal(row);
    set(state => ({
      proposals: state.proposals.map(p => (p.id === proposalId ? updated : p)),
      isLoading: false,
      error: null,
    }));
  } else {
    set({ isLoading: false, error: null });
  }
  return true;
}

export const useProposalPacketStore = create<ProposalPacketState>()((set, get) => ({
  proposals: [],
  threadEntries: [],
  isLoading: false,
  error: null,

  // ========================================================================
  // Proposal CRUD
  // ========================================================================

  fetchProposals: async (cycleId: string) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return;

    set({ isLoading: true, error: null });

    try {
      const { data, error } = await supabase
        .from('catchball_proposals')
        .select('*')
        .eq('cycle_id', cycleId)
        .order('proposed_at', { ascending: false });

      if (error) {
        console.error('[ProposalPacketStore] Fetch error:', error);
        set({ isLoading: false, error: error.message });
        return;
      }

      const proposals = (data || []).map(mapRowToProposal);
      // Merge into state: the list also holds other cycles' proposals, which
      // are not this read's to drop.
      set(state => ({
        proposals: [...state.proposals.filter(p => p.cycleId !== cycleId), ...proposals],
        isLoading: false,
        error: null,
      }));
      console.log(`[ProposalPacketStore] Fetched ${proposals.length} proposals for cycle ${cycleId}`);
    } catch (err) {
      console.error('[ProposalPacketStore] Fetch exception:', err);
      set({ isLoading: false, error: String(err) });
    }
  },

  fetchProposalsForWorkspace: async (workspaceId: string) => {
    if (!getSafeSupabaseClient()) return;
    const supabase = getTypedSupabaseClient();

    try {
      const rows: Record<string, unknown>[] = [];
      for (let from = 0; ; from += PROPOSALS_PAGE_SIZE) {
        const { data, error } = await supabase
          .from('catchball_proposals')
          .select('*, degap_cycles!inner(workspace_id)')
          .eq('degap_cycles.workspace_id', workspaceId)
          .order('proposed_at', { ascending: false })
          .order('id')
          .range(from, from + PROPOSALS_PAGE_SIZE - 1);

        if (error) {
          console.error('[ProposalPacketStore] Fetch for workspace error:', error);
          return;
        }

        rows.push(...(data ?? []));
        if ((data ?? []).length < PROPOSALS_PAGE_SIZE) break;
      }

      set({ proposals: rows.map(mapRowToProposal) });
    } catch (err) {
      console.error('[ProposalPacketStore] Fetch for workspace exception:', err);
    }
  },

  fetchProposalsForLoop: async (loopId: string) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return [];

    try {
      const { data, error } = await supabase
        .from('catchball_proposals')
        .select('*')
        .eq('loop_id', loopId)
        .order('proposed_at', { ascending: false });

      if (error) {
        console.error('[ProposalPacketStore] Fetch for loop error:', error);
        return [];
      }

      const proposals = (data || []).map(mapRowToProposal);
      
      // Merge into state
      set(state => {
        const otherProposals = state.proposals.filter(p => p.loopId !== loopId);
        return { proposals: [...otherProposals, ...proposals] };
      });

      return proposals;
    } catch (err) {
      console.error('[ProposalPacketStore] Fetch for loop exception:', err);
      return [];
    }
  },

  fetchProposalById: async (proposalId: string) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return null;
    try {
      const { data, error } = await supabase
        .from('catchball_proposals')
        .select('*')
        .eq('id', proposalId)
        .single();
      if (error || !data) {
        if (error) console.error('[ProposalPacketStore] Fetch by id error:', error);
        return null;
      }
      const proposal = mapRowToProposal(data);
      set(state => {
        const exists = state.proposals.some(p => p.id === proposalId);
        const proposals = exists
          ? state.proposals.map(p => (p.id === proposalId ? proposal : p))
          : [proposal, ...state.proposals];
        return { proposals };
      });
      return proposal;
    } catch (err) {
      console.error('[ProposalPacketStore] Fetch by id exception:', err);
      return null;
    }
  },

  getProposalsByObjective: (objectiveId: string) => {
    return get().proposals.filter(p => 
      p.proposalType === 'packet' && 
      p.objectiveIds?.includes(objectiveId)
    );
  },

  getPacketProposals: (cycleId: string) => {
    return get().proposals.filter(p => 
      p.cycleId === cycleId && 
      p.proposalType === 'packet'
    );
  },

  createProposalPacket: async (params: CreateProposalPacketParams) => {
    set({ isLoading: true, error: null });

    try {
      // Readiness is a disclosure the reviewer sees; it is computed from the
      // same reads either side would make and travels with the packet.
      const readinessSnapshot = await get().computeReadinessSnapshot(
        params.cycleId,
        params.itemIds || [],
      );
      const hasGaps = readinessSnapshot.summary.itemsWithGaps > 0;
      const missingLock = readinessSnapshot.summary.missingCriteriaLock;
      const comparabilityState = (hasGaps || missingLock) ? 'pre_comparable' : 'comparable';

      // §2.3a: one server transaction (catchball_draft_proposal, mig 772) —
      // the same path "Send this up" uses, in 'ost' mode. The old direct
      // INSERT left proposal_kind on its 'ITEM_VALUE' default with no
      // item_id, which catchball_proposals_kind_item_id_ck refused, so this
      // screen could never have saved anything.
      const result = await draftCatchballProposal({
        loopId: params.loopId,
        mode: 'ost',
        fields: {
          objective_ids: params.objectiveIds,
          ost_node_ids: params.ostNodeIds,
          rationale: params.rationale,
          readiness_snapshot: readinessSnapshot,
          comparability_state: comparabilityState,
        },
      });

      if (!result.ok) {
        console.error('[ProposalPacketStore] catchball_draft_proposal refused:', result.error);
        set({ isLoading: false, error: result.error ?? 'The proposal could not be built.' });
        return null;
      }

      const row = (result.data as { proposal?: Record<string, unknown> } | undefined)?.proposal;
      if (!row) {
        set({ isLoading: false, error: 'The proposal could not be built.' });
        return null;
      }
      const createdProposal = mapRowToProposal(row);

      set(state => ({
        proposals: [createdProposal, ...state.proposals],
        isLoading: false,
        error: null,
      }));

      console.log(`[ProposalPacketStore] Created proposal packet ${createdProposal.id}`);
      return createdProposal;
    } catch (err) {
      console.error('[ProposalPacketStore] Create exception:', err);
      set({ isLoading: false, error: String(err) });
      return null;
    }
  },

  /**
   * Submit proposal (authority transition). Per Build Contract §2.3a, runs via
   * transactional RPC catchball_submit_proposal; updates local state from result.
   */
  submitProposal: async (proposalId: string, actorId: string) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return false;

    const proposal = get().proposals.find(p => p.id === proposalId);
    if (!proposal || !proposal.loopId) return false;

    try {
      // The one shared submit path (src/lib/catchball/proposalDecisions.ts).
      const outcome = await submitCatchballProposal({ proposalId, actorId });
      if (!outcome.ok) {
        set({ error: outcome.error ?? 'Submit failed' });
        return false;
      }
      const result = outcome.data as { session_report_id?: string } | undefined;

      set(state => ({
        proposals: state.proposals.map(p =>
          p.id === proposalId
            ? { ...p, status: 'pending' as const, sessionReportId: result?.session_report_id ?? undefined }
            : p
        ),
        error: null,
      }));

      return true;
    } catch (err) {
      console.error('[ProposalPacketStore] Submit exception:', err);
      return false;
    }
  },

  // ========================================================================
  // Executive Responses (via RPC for transactional integrity)
  // 
  // Per Build Contract:
  // - Do not orchestrate multi-write state changes in Zustand
  // - Each action writes: thread entry + proposal update + loop update atomically
  // - UI calls exactly one command endpoint per action
  // - UI re-reads and renders from DB truth returned by the call
  // ========================================================================

  acceptProvisional: async (params: ExecutiveResponseParams) => {
    set({ isLoading: true, error: null });
    // The one accept (763), provisional: nothing is ratified yet; the ball goes
    // back to the working tier with any blockers still to clear. Ratifying is
    // catchballStore.acceptProposal (the same RPC, not provisional).
    const result = await acceptCatchballProposal({
      proposalId: params.proposalId,
      provisional: true,
      conditions: params.message ?? null,
      blockedReasons: params.structuredBlockers ?? null,
      actorName: params.actorName ?? null,
    });
    return applyDecision(set, params.proposalId, result);
  },

  returnWithBlockers: async (params: ExecutiveResponseParams) => {
    if (params.responseType !== 'return' || !params.structuredBlockers) {
      console.error('[ProposalPacketStore] Return requires structured blockers');
      set({ error: 'Say what needs to change before returning it.' });
      return false;
    }
    set({ isLoading: true, error: null });
    const result = await returnCatchballProposal({
      proposalId: params.proposalId,
      revisionGuidance: params.message ?? null,
      structuredBlockers: params.structuredBlockers,
      actorName: params.actorName ?? null,
    });
    return applyDecision(set, params.proposalId, result);
  },

  rejectProposal: async (params: ExecutiveResponseParams) => {
    if (!params.message?.trim()) {
      set({ error: 'Rejection reason is required' });
      return false;
    }
    set({ isLoading: true, error: null });
    const result = await rejectCatchballProposal({
      proposalId: params.proposalId,
      rationale: params.message,
      actorName: params.actorName ?? null,
    });
    return applyDecision(set, params.proposalId, result);
  },

  // ========================================================================
  // Readiness Computation
  // ========================================================================

  computeReadinessSnapshot: async (cycleId: string, itemIds: string[]): Promise<ReadinessSnapshot> => {
    const supabase = getSafeSupabaseClient();
    const now = new Date().toISOString();

    const snapshot: ReadinessSnapshot = {
      snapshotAt: now,
      cycleId,
      items: {},
      summary: {
        totalItems: itemIds.length,
        itemsReady: 0,
        itemsWithGaps: 0,
        missingCriteriaLock: false,
        missingRanges: [],
      },
    };

    if (!supabase || itemIds.length === 0) {
      return snapshot;
    }

    try {
      // Get cycle progress for criteria lock status
      const { data: progressData } = await supabase
        .from('cycle_progress')
        .select('criteria_locked, criteria_missing_scales')
        .eq('cycle_id', cycleId)
        .single();

      const criteriaLocked = progressData?.criteria_locked || false;
      const missingRanges = progressData?.criteria_missing_scales || [];

      snapshot.summary.missingCriteriaLock = !criteriaLocked;
      snapshot.summary.missingRanges = missingRanges;

      // Get per-item readiness using the SQL function
      for (const itemId of itemIds) {
        const readiness = await get().getItemReadiness(cycleId, itemId);
        if (readiness) {
          snapshot.items[itemId] = readiness;
          
          // Update summary
          const hasGaps = 
            readiness.valueCoverage.missingRequired.length > 0 ||
            readiness.valueCoverage.proposedOnly.length > 0 ||
            readiness.altitudeAlignment === 'out_of_scope';
          
          if (hasGaps) {
            snapshot.summary.itemsWithGaps++;
          } else {
            snapshot.summary.itemsReady++;
          }
        }
      }

      return snapshot;
    } catch (err) {
      console.error('[ProposalPacketStore] Compute readiness error:', err);
      return snapshot;
    }
  },

  getItemReadiness: async (cycleId: string, itemId: string): Promise<ItemReadiness | null> => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return null;

    try {
      // Call the SQL function
      const { data, error } = await supabase
        .rpc('get_item_readiness', {
          p_cycle_id: cycleId,
          p_item_id: itemId,
        });

      if (error) {
        console.error('[ProposalPacketStore] Get item readiness error:', error);
        return null;
      }

      return data as ItemReadiness;
    } catch (err) {
      console.error('[ProposalPacketStore] Get item readiness exception:', err);
      return null;
    }
  },

  // ========================================================================
  // Thread Management
  // ========================================================================

  fetchThreadForProposal: async (proposalId: string) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return [];

    try {
      const { data, error } = await supabase
        .from('catchball_threads')
        .select('*')
        .eq('proposal_id', proposalId)
        .order('created_at', { ascending: true });

      if (error) {
        console.error('[ProposalPacketStore] Fetch thread error:', error);
        return [];
      }

      const entries = (data || []).map(mapRowToThreadEntry);
      
      // Merge into state
      set(state => {
        const otherEntries = state.threadEntries.filter(e => e.proposalId !== proposalId);
        return { threadEntries: [...otherEntries, ...entries] };
      });

      return entries;
    } catch (err) {
      console.error('[ProposalPacketStore] Fetch thread exception:', err);
      return [];
    }
  },

  // ========================================================================
  // Selectors
  // ========================================================================

  getProposal: (proposalId: string) => {
    return get().proposals.find(p => p.id === proposalId);
  },

  getProposalSummaries: (cycleId: string): ProposalSummary[] => {
    return get().proposals
      .filter(p => p.cycleId === cycleId)
      .map(p => ({
        id: p.id,
        proposalType: p.proposalType,
        proposedByName: p.proposedByName,
        proposedAt: p.proposedAt,
        status: p.status,
        objectiveCount: p.objectiveIds?.length,
        itemCount: Object.keys(p.readinessSnapshot?.items || {}).length,
        readinessSummary: p.readinessSnapshot?.summary ? {
          ready: p.readinessSnapshot.summary.itemsReady,
          withGaps: p.readinessSnapshot.summary.itemsWithGaps,
          total: p.readinessSnapshot.summary.totalItems,
        } : undefined,
        comparabilityState: p.comparabilityState,
        itemTitle: p.itemTitle,
        proposedAction: p.proposedAction,
      }));
  },

  getObjectivesWithProposals: (cycleId: string): ObjectiveWithProposals[] => {
    const proposals = get().getPacketProposals(cycleId);
    
    // Group by objective
    const byObjective = new Map<string, CatchballProposal[]>();
    
    for (const p of proposals) {
      for (const objId of p.objectiveIds || []) {
        const existing = byObjective.get(objId) || [];
        existing.push(p);
        byObjective.set(objId, existing);
      }
    }
    
    // Convert to ObjectiveWithProposals
    return Array.from(byObjective.entries()).map(([objectiveId, objProposals]) => ({
      objectiveId,
      objectiveName: `Objective ${objectiveId.slice(0, 8)}`, // Placeholder - should fetch from objectives store
      proposals: objProposals.map(p => ({
        id: p.id,
        proposalType: p.proposalType,
        proposedByName: p.proposedByName,
        proposedAt: p.proposedAt,
        status: p.status,
        comparabilityState: p.comparabilityState,
      })),
      pendingCount: objProposals.filter(p => p.status === 'pending').length,
      returnedCount: objProposals.filter(p => p.status === 'returned' || p.status === 'countered').length,
      acceptedCount: objProposals.filter(p => p.status === 'accepted').length,
    }));
  },

  clearError: () => set({ error: null }),
}));

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Fetch full proposal context via RPC (includes loop, threads, next steps, who-has-ball).
 * Used by UI to display complete proposal state after actions.
 */
export async function fetchProposalContext(proposalId: string): Promise<{
  proposal: CatchballProposal | null;
  loop: Record<string, unknown> | null;
  threads: CatchballThreadEntry[];
  nextSteps: Array<{
    id: string;
    sourceEvent: string;
    suggestedAction: string;
    suggestedPhase: string | null;
    blockedReasonsSnapshot: Record<string, unknown> | null;
    createdAt: string;
    resolvedAt: string | null;
  }>;
  whoHasBall: { scope: string | null; userId: string | null } | null;
} | null> {
  const supabase = getSafeSupabaseClient();
  if (!supabase) return null;

  try {
    const { data, error } = await supabase.rpc('get_proposal_packet_context', {
      p_packet_id: proposalId,
    });

    if (error) {
      console.error('[ProposalPacketStore] Fetch context error:', error);
      return null;
    }

    return {
      proposal: data.proposal ? mapRowToProposal(data.proposal) : null,
      loop: data.loop,
      threads: (data.threads || []).map((t: Record<string, unknown>) => ({
        id: t.id as string,
        messageType: t.messageType as CatchballThreadEntry['messageType'],
        authorRole: t.authorRole as CatchballThreadEntry['authorRole'],
        authorId: t.startedBy as string,
        body: '',
        structuredBlockers: t.structuredBlockers as StructuredBlockers | undefined,
        createdAt: new Date(t.startedAt as string),
        cycleId: '',
      })),
      nextSteps: data.nextSteps || [],
      whoHasBall: data.whoHasBall,
    };
  } catch (err) {
    console.error('[ProposalPacketStore] Fetch context exception:', err);
    return null;
  }
}

